import { JointAccountHolderEligibility } from "./graphql-types/JointAccountHolderEligibility";
import { AccountStatus, AccountTypeChoices } from "./graphql-types/globalTypes";

const SUPPORTED_SUPPLIES = new Set([
  "ELECTRICITY",
  "GAS",
  "EMBEDDED_ELECTRICITY",
  "HOT_WATER",
]);
const PRIMARY_ROLES = new Set(["ADMIN", "PRIMARY_NO_COMMS"]);

export interface JointAccountHolderAccount {
  accountNumber: string;
  serviceType: string;
  address: string;
}

export interface JointAccountHolderEligibilityResult {
  accounts: JointAccountHolderAccount[];
  eligible: boolean;
  hasHardship: boolean;
}

const UNAVAILABLE: JointAccountHolderEligibilityResult = {
  accounts: [],
  eligible: false,
  hasHardship: false,
};

/** Evaluate only the current viewer's relationships; never infer a primary role. */
export const determineJointAccountHolderEligibility = (
  data: JointAccountHolderEligibility | undefined
): JointAccountHolderEligibilityResult => {
  const viewer = data?.viewer;
  if (
    !viewer ||
    viewer.hasFamilyIssues !== false ||
    !viewer.accounts ||
    !viewer.accountUserRoles ||
    !viewer.portfolioUserRoles ||
    viewer.accounts.some((account) => !account) ||
    viewer.accountUserRoles.some((role) => !role || !role.role) ||
    viewer.portfolioUserRoles.some((role) => !role || !role.role)
  ) {
    return UNAVAILABLE;
  }

  const qualifyingAccounts = viewer.accounts.filter((account) => {
    if (
      !account?.number ||
      account.status !== AccountStatus.ACTIVE ||
      (account.accountType !== AccountTypeChoices.DOMESTIC &&
        account.accountType !== AccountTypeChoices.OCCUPIER) ||
      !account.agreements?.some(
        (agreement) =>
          agreement?.supplyType && SUPPORTED_SUPPLIES.has(agreement.supplyType)
      )
    ) {
      return false;
    }

    // Kraken sync combines account and matching portfolio roles. A secondary
    // account role does not override a primary role on its portfolio.
    return (
      viewer.accountUserRoles!.some(
        (role) =>
          role?.account.number === account.number &&
          PRIMARY_ROLES.has(role.role)
      ) ||
      viewer.portfolioUserRoles!.some(
        (role) =>
          !!account.portfolio?.number &&
          role?.portfolio.number === account.portfolio.number &&
          PRIMARY_ROLES.has(role.role)
      )
    );
  });

  if (
    qualifyingAccounts.length === 0 ||
    qualifyingAccounts.some(
      (account) =>
        !account?.activeHardshipAgreements ||
        account.activeHardshipAgreements.some((agreement) => !agreement)
    )
  ) {
    return UNAVAILABLE;
  }

  return {
    accounts: Array.from(
      new Map(
        qualifyingAccounts.map((account) => [
          account!.number!,
          {
            accountNumber: account!.number!,
            serviceType: Array.from(
              new Set(
                account!
                  .agreements!.filter(
                    (agreement) =>
                      agreement?.supplyType &&
                      SUPPORTED_SUPPLIES.has(agreement.supplyType)
                  )
                  .map(
                    (agreement) =>
                      ({
                        ELECTRICITY: "Electricity",
                        GAS: "Natural gas",
                        EMBEDDED_ELECTRICITY: "Electricity",
                        HOT_WATER: "Hot water",
                      }[agreement!.supplyType!]!)
                  )
              )
            ).join(" / "),
            address: Array.from(
              new Set(
                (account!.properties ?? [])
                  .map((property) => property?.address?.trim())
                  .filter((address): address is string => !!address)
              )
            ).join("; "),
          },
        ])
      ).values()
    ),
    eligible: true,
    hasHardship: qualifyingAccounts.some(
      (account) => account!.activeHardshipAgreements!.length > 0
    ),
  };
};
